import { Link, useNavigate } from "react-router-dom";
import { useProducts } from "../../hooks/useProducts";
import { useState } from "react";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { ProductService } from "../../services/productService";
import { Package, Pencil, Trash2, PlusCircle, Filter, Search, ShoppingBag } from "lucide-react";

const AdminInventoryPage = () => {
  const navigate = useNavigate();
  const { products, isLoading, error, refetch } = useProducts();
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");

  const handleDeleteProduct = async () => {
    try {
      await ProductService.deleteProductById(deletingProductId);
      refetch();
      setDeletingProductId(null);
    } catch (error) {
      console.error("Failed to delete product:", error);
      // Using a more user-friendly error notification
      setErrorMessage("Unable to delete product. Please try again later.");
      setShowErrorToast(true);
    }
  };

  const confirmDeleteProduct = (productId:string) => {
    setDeletingProductId(productId);
    setShowDialog(true);
  };

  const handleConfirm = () => {
    if (deletingProductId) {
      handleDeleteProduct();
    }
    setShowDialog(false);
  };

  const handleCancel = () => {
    setDeletingProductId(null);
    setShowDialog(false);
  };

  // For toast notifications
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Filter products based on search and filter criteria
  const filteredProducts = products?.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterBy === "all") return matchesSearch;
    if (filterBy === "low-stock" && (product.stock || 0) <= 10) return matchesSearch;
    if (filterBy === "out-of-stock" && (product.stock || 0) === 0) return matchesSearch;
    if (filterBy === "in-stock" && (product.stock || 0) > 10) return matchesSearch;
    
    return false;
  })||[];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-md text-center">
          <div className="text-red-500 text-6xl mb-4">!</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">We couldn't load your products. Please try again later.</p>
          <button 
            onClick={() => refetch()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if there are no products at all (empty inventory)
  const hasNoProducts = products?.length === 0;

  // Empty inventory state - shown when there are no products at all
  if (hasNoProducts) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <div className="inline-flex items-center justify-center p-4 bg-blue-100 rounded-full mb-4">
            <ShoppingBag className="h-10 w-10 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Products Found</h2>
          <p className="text-gray-600 mb-6">
            Your inventory is currently empty. Start by adding your first product to your store.
          </p>
          <button
            onClick={() => navigate("/admin/add-product")}
            className="w-full md:w-auto px-6 py-3 text-base font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors inline-flex items-center justify-center gap-2"
          >
            <PlusCircle className="w-5 h-5" />
            <span>Add Your First Product</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header section */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-md">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Inventory Management</h1>
            </div>
            <button
              onClick={() => navigate("/admin/add-product")}
              className="inline-flex items-center justify-center w-full md:w-auto px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors gap-2"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Add New Product</span>
            </button>
          </div>
          
          {/* Search and filters */}
          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
              >
                <option value="all">All Products</option>
                <option value="in-stock">In Stock</option>
                <option value="low-stock">Low Stock</option>
                <option value="out-of-stock">Out of Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts?.length > 0 ? (
                  filteredProducts.map((product, index) => (
                    <tr
                      key={product.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 rounded-md bg-gray-100 overflow-hidden">
                            <img
                              className="h-full w-full object-cover"
                              src={product.images[0]}
                              alt={product.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).onerror = null;
                                (e.target as HTMLImageElement).src = "https://via.placeholder.com/40?text=No+Image";
                              }}
                            />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 line-clamp-2">
                              {product.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {product.id.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{(product.mrp || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {(product.discountPercent || 0) > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {product.discountPercent}% OFF
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">No discount</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${
                            (product.stock || 0) > 10
                              ? "bg-green-100 text-green-800"
                              : (product.stock || 0) > 0
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {(product.stock || 0) === 0 
                            ? "Out of stock" 
                            : (product.stock || 0) <= 10 
                            ? `Low stock (${product.stock || 0})` 
                            : `${product.stock || 0} in stock`}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`../add-product?id=${product.id}`}
                            state={product}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors gap-1"
                            aria-label={`Edit ${product.title}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Edit</span>
                          </Link>
                          <button
                            onClick={() => confirmDeleteProduct(product.id)}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors gap-1"
                            aria-label={`Delete ${product.title}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="max-w-sm mx-auto">
                        <div className="flex flex-col items-center">
                          <div className="p-3 bg-blue-50 rounded-full mb-3">
                            <Package className="w-10 h-10 text-blue-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-1">No matching products found</h3>
                          <p className="text-sm text-gray-500 mb-6 text-center">
                            {searchTerm 
                              ? `We couldn't find any products matching "${searchTerm}"` 
                              : "No products match the selected filter criteria"}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            {searchTerm && (
                              <button
                                onClick={() => setSearchTerm("")}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                Clear Search
                              </button>
                            )}
                            <button
                              onClick={() => navigate("/admin/add-product")}
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center gap-1.5"
                            >
                              <PlusCircle className="w-4 h-4" />
                              <span>Add New Product</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination - Simple version */}
          {/* {filteredProducts?.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">1</span> to{" "}
                    <span className="font-medium">{filteredProducts.length}</span> of{" "}
                    <span className="font-medium">{filteredProducts.length}</span> results
                  </p>
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Confirmation Dialog for deletion */}
        <ConfirmationDialog
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isOpen={showDialog}
          title="Delete Product"
        />
        
        {/* Error Toast */}
        {showErrorToast && (
          <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
            <div className="flex">
              <div className="py-1">
                <svg className="h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-bold">Error</p>
                <p className="text-sm">{errorMessage}</p>
              </div>
              <button 
                onClick={() => setShowErrorToast(false)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInventoryPage;